import React, { useEffect, useRef } from 'react';
import { Helmet } from "react-helmet-async";
import {
    Users,
    Briefcase,
    Building,
    Award,
    Layers,
    Globe,
    Sliders,
    Tv,
    Sparkles,
    Eye,
    Shield,
    Image as ImageIcon
} from 'lucide-react';

const corporateServices = [
    { id: '01', icon: Users, title: 'Polished Executive Headshots', desc: 'Crafting commanding, authentic portraits of C-suite leaders and board members that project approachability, high competence, and corporate alignment.' },
    { id: '02', icon: Briefcase, title: 'Workplace & Culture Realism', desc: 'Documenting collaborative teams, agile work patterns, and dynamic internal interactions with a polished, authentic, non-staged documentary style.' },
    { id: '03', icon: Building, title: 'Corporate Facility & Architecture', desc: 'Capturing impressive exterior office silhouettes, clean manufacturing plants, and sprawling industrial complexes to illustrate real enterprise assets.' },
    { id: '04', icon: Award, title: 'Annual Report & PR Assets', desc: 'Providing highly specific, impact-driven visual content optimized for stakeholder distributions, investor presentations, and major media feature packages.' },
    { id: '05', icon: Globe, title: 'Multi-Location Brand Consistency', desc: 'Deploying uniform lighting setups and background templates to ensure multi-national teams look visually unified across globally distributed offices.' },
    { id: '06', icon: Sparkles, title: 'Keynote & High-Profile Event Capture', desc: 'Freezing critical moments from major annual general meetings, product reveals, panel discussions, and high-prestige executive network events.' },
];

const corporateDeliverables = [
    { id: '01', icon: Tv, title: 'Instant On-Set Digital Proofing', desc: 'Tethering high-resolution camera systems directly to tablets so busy executives can audit, approve, and select their preferred hero shot in real time.' },
    { id: '02', icon: Sliders, title: 'Micro Retouching & Blemish Clean', desc: 'Polishing professional portraits cleanly by smoothing clothing creases, cleaning flyaway hairs, and correcting glare on lenses while retaining an authentic look.' },
    { id: '03', icon: Shield, title: 'Strict Enterprise NDA Compliance', desc: 'Enforcing locked-down on-set data workflows to ensure unreleased products, secure wall displays, and proprietary boards remain entirely confidential.' },
    { id: '04', icon: Layers, title: 'Rapid High-Volume Batch Workflows', desc: 'Systematizing production schedules to efficiently run dozens of team members through professional styling and shooting lanes within a single business day.' },
    { id: '05', icon: Building, title: 'Environmental Lighting Balancing', desc: 'Seamlessly matching portable studio strobe banks with harsh overhead workspace fluorescent lights to deliver clean skin tones without office color casts.' },
    { id: '06', icon: Eye, title: 'Detailed Pre-Production Location Assays', desc: 'Scouting out corporate lobbies, conference rooms, and exterior architecture prior to shoot day to accurately map light paths and production flow lines.' },
    { id: '07', icon: Globe, title: 'Multi-Format Asset Optimization', desc: 'Delivering every final selection cleanly cropped for standard print layouts, horizontal website headers, circular LinkedIn avatars, and mobile profiles.' },
    { id: '08', icon: Users, title: 'Tailored Wardrobe & Styling Blueprints', desc: 'Providing teams with curated styling directives covering optimal patterns, colors, and textures to avoid camera distortion artifacts.' },
    { id: '09', icon: ImageIcon, title: 'Secure Metadata-Tagged Cloud Vaults', desc: 'Organizing thousands of raw and master files inside secure cloud spaces indexed with department keywords for simple global communications retrieval.' },
];

const CorporatePhotographyPortfolio = () => {
    const viewportRef = useRef(null);
    const leftStripRef = useRef(null);
    const rightStripRef = useRef(null);

    useEffect(() => {
        const viewport = viewportRef.current;
        const leftStrip = leftStripRef.current;
        const rightStrip = rightStripRef.current;

        if (!viewport || !leftStrip || !rightStrip) return;

        let currentPosLeft = 0;
        let currentPosRight = 0;
        let isHovered = false;
        let animationFrameId;

        const speedLeft = 0.50;
        const speedRight = 0.30;

        const handleMouseEnter = () => { isHovered = true; };
        const handleMouseLeave = () => { isHovered = false; };

        viewport.addEventListener('mouseenter', handleMouseEnter);
        viewport.addEventListener('mouseleave', handleMouseLeave);

        function performAutoScroll() {
            if (!isHovered && leftStrip && rightStrip) {
                currentPosLeft -= speedLeft;
                currentPosRight -= speedRight;

                if (Math.abs(currentPosLeft) >= (leftStrip.scrollHeight / 2)) {
                    currentPosLeft = 0;
                }
                if (Math.abs(currentPosRight) >= (rightStrip.scrollHeight / 2)) {
                    currentPosRight = 0;
                }

                if (window.innerWidth > 991) {
                    leftStrip.style.transform = `translateY(${currentPosLeft}px)`;
                    rightStrip.style.transform = `translateY(${currentPosRight}px)`;
                } else {
                    leftStrip.style.transform = 'none';
                    rightStrip.style.transform = 'none';
                }
            }
            animationFrameId = requestAnimationFrame(performAutoScroll);
        }

        animationFrameId = requestAnimationFrame(performAutoScroll);

        return () => {
            cancelAnimationFrame(animationFrameId);
            if (viewport) {
                viewport.removeEventListener('mouseenter', handleMouseEnter);
                viewport.removeEventListener('mouseleave', handleMouseLeave);
            }
        };
    }, []);

    return (
        <div>

            <Helmet>
                <title>Corporate Photography Portfolio in Hyderabad | KhultStudios</title>

                <meta
                    name="description"
                    content="Explore KhultStudios' Corporate Photography Portfolio featuring executive headshots, corporate events, office branding, business portraits, team photography, conferences, industrial facilities, and commercial photography in Hyderabad."
                />

                <meta
                    name="keywords"
                    content="Corporate Photography Portfolio Hyderabad, Corporate Photography Hyderabad, Corporate Photographer Hyderabad, Business Photography, Executive Headshots Hyderabad, Professional Headshots, Office Photography, Team Photography, Company Profile Photography, Corporate Event Photography, Conference Photography, Industrial Photography, Commercial Photography Hyderabad, Business Branding Photography, KhultStudios"
                />

                <meta name="robots" content="index, follow" />
                <meta name="author" content="KhultStudios" />

                <link
                    rel="canonical"
                    href="https://khultstudios.com/corporate-photography-portfolio"
                />

                {/* Open Graph */}
                <meta property="og:type" content="website" />
                <meta
                    property="og:url"
                    content="https://khultstudios.com/corporate-photography-portfolio"
                />
                <meta
                    property="og:title"
                    content="Corporate Photography Portfolio in Hyderabad | KhultStudios"
                />
                <meta
                    property="og:description"
                    content="Browse our portfolio of executive headshots, corporate branding, office photography, team portraits, conferences, and commercial business photography in Hyderabad."
                />
                <meta
                    property="og:image"
                    content="https://khultstudios.com/images/corporate-photography-portfolio-banner.jpg"
                />
                <meta property="og:site_name" content="KhultStudios" />

                {/* Twitter */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta
                    name="twitter:title"
                    content="Corporate Photography Portfolio | KhultStudios"
                />
                <meta
                    name="twitter:description"
                    content="Discover our portfolio of corporate headshots, office branding, business events, and executive photography in Hyderabad."
                />
                <meta
                    name="twitter:image"
                    content="https://khultstudios.com/images/corporate-photography-portfolio-banner.jpg"
                />

                {/* Favicon */}
                <link rel="icon" href="https://khultstudios.com/favicon.ico" />
            </Helmet>
            {/* FULLSCREEN VIDEO VIEWPORT BACKGROUND ENGINE */}
            <div className="video-bg-viewport">
                <video
                    autoPlay
                    loop
                    muted
                    playsInline
                    className="global-html5-video"
                >
                    <source src="videos/corporate_brand_reel.mp4" type="video/mp4" />
                    Your browser does not support the video tag.
                </video>
                <div className="vignette-overlay-layer" />
            </div>

            {/* INNER APP CANVAS CONTAINER SECTION */}
            <div className="page-frame px-2 px-md-5 pb-5">
                <div className="landing-container position-relative overflow-hidden">
                    <div className="paper-texture" />
                    <div className="container-fluid h-100 p-4 p-lg-5 position-relative z-2">
                        <div className="row h-100 gy-5">

                            {/* Content Brief side description */}
                            <div className="col-lg-5 d-flex flex-column justify-content-center h-100 content-side">
                                <div className="invitation-text position-relative py-4">
                                    <h5 className="text-uppercase tracking-wider text-orange fw-bold mb-2">
                                        Enterprise Communication & Brand Architecture
                                    </h5>
                                    <h1 className="display-3 text-uppercase main-title text-white mb-4">
                                        Visual Assets to Define Corporate Value
                                    </h1>
                                    <p className="lead-text text-light-gray mb-4 col-xl-10">
                                        Modern brand value hinges entirely on human trust. Corporate identity requires a fine balance between calculated competence and authentic human connection. Outdated corporate photography, flat generic lighting, or obviously forced office poses convey a lack of transparency and an uninspired workplace culture.
                                    </p>
                                    <p className="lead-text text-light-gray mb-4 col-xl-10">
                                        At KhultStudio, we craft professional, high-fidelity corporate asset ecosystems. By pairing rapid real-time on-set digital proofing with precise environmental light matching and strict NDA protocols, we supply leading organizations with polished, cohesive visual stories that command respect from investors and media alike.
                                    </p>
                                    <a
                                        href="#details"
                                        className="btn btn-orange text-uppercase px-4 py-2 text-dark fw-bold tracking-wide btn-scroll"
                                    >
                                        Review Enterprise Assets
                                    </a>
                                </div>
                                <div className="social-footer d-flex gap-3 mt-2">
                                    <a href="#" className="social-icon"><i className="fab fa-facebook-f" /></a>
                                    <a href="#" className="social-icon"><i className="fab fa-linkedin-in" /></a>
                                    <a href="#" className="social-icon"><i className="fab fa-twitter" /></a>
                                </div>
                            </div>

                            {/* Infinite Slanted Ribbon Gallery Strip components */}
                            <div className="col-lg-7 position-relative photo-zone-height">
                                <div className="filmstrip-skew-box">
                                    <div
                                        className="vertical-scroll-viewport"
                                        ref={viewportRef}
                                    >
                                        <div className="row gx-4 h-100">
                                            <div className="col-6">
                                                <div className="filmstrip-ribbon" ref={leftStripRef} style={{ display: 'none' }}>
                                                    <div className="ribbon-cell">
                                                        <img src="/corporate-photography/corporate-photography.jpg" alt="Wedding 5" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="/corporate-photography/corporate-photography1.jpg" alt="Wedding 6" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="/corporate-photography/corporate-photography2.jpg" alt="Wedding 7" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="/corporate-photography/corporate-photography3.jpg" alt="Wedding 8" />
                                                    </div>
                                                    {/* Clone Buffers */}
                                                    <div className="ribbon-cell">
                                                        <img src="/corporate-photography/corporate-photography.jpg" alt="Wedding 5 Buffer" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="/corporate-photography/corporate-photography1.jpg" alt="Wedding 6 Buffer" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="/corporate-photography/corporate-photography2.jpg" alt="Wedding 7 Buffer" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="/corporate-photography/corporate-photography3.jpg" alt="Wedding 8 Buffer" />
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-6">
                                                <div className="filmstrip-ribbon ribbon-offset" ref={rightStripRef}>
                                                    <div className="ribbon-cell">
                                                        <img src="/corporate-photography/corporate-photography.jpg" alt="Wedding 5" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="/corporate-photography/corporate-photography1.jpg" alt="Wedding 6" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="/corporate-photography/corporate-photography2.jpg" alt="Wedding 7" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="/corporate-photography/corporate-photography3.jpg" alt="Wedding 8" />
                                                    </div>
                                                    {/* Clone Buffers */}
                                                    <div className="ribbon-cell">
                                                        <img src="/corporate-photography/corporate-photography.jpg" alt="Wedding 5 Buffer" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="/corporate-photography/corporate-photography1.jpg" alt="Wedding 6 Buffer" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="/corporate-photography/corporate-photography2.jpg" alt="Wedding 7 Buffer" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="/corporate-photography/corporate-photography3.jpg" alt="Wedding 8 Buffer" />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                        </div>
                    </div>
                </div>
            </div>

            {/* CORPORATE SERVICES SECTOR SECTION */}
            <section className="services-section py-5" id="details">
                <div className="container">
                    <h2 className="text-center mb-5 text-white">💼 Strategic B2B Representation Models</h2>
                    <p className="text-light-gray mt-3 text-center max-w-2xl mx-auto">
                        Different corporate touchpoints require distinct asset formatting. Our operational frameworks adapt directly to digital channels, print communications, and international corporate governance environments.
                    </p>
                    <div className="row g-4 mt-2">
                        {corporateServices.map((service, index) => (
                            <div key={index} className="col-md-4">
                                <div className={`service-card ${index === 0 ? 'active' : ''}`}>
                                    <div className="card-content">
                                        <service.icon className="icon mb-3" size={40} />
                                        <h3>{service.title}</h3>
                                        <p>{service.desc}</p>
                                    </div>
                                    <span className="card-number">{service.id}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* TECHNICAL EXECUTION & PRODUCTION COMPLIANCE VAULTS */}
            <section className="comprehensive-section py-5 bg-black-opacity-50">
                <div className="container">
                    <h2 className="text-center mb-5 text-white">⚙️ Corporate Execution Systems & Technical Pipelines</h2>
                    <div className="row g-4">
                        {corporateDeliverables.map((service, index) => (
                            <div key={index} className="col-lg-4 col-md-6">
                                <div className="service-card">
                                    <div className="card-content">
                                        <service.icon className="icon mb-3" size={40} />
                                        <h4>{service.title}</h4>
                                        <p>{service.desc}</p>
                                    </div>
                                    <span className="card-number">{service.id}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
        </div>
    );
};

export default CorporatePhotographyPortfolio;